import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Check, X, Trash2, Pencil, BookOpen, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const CATEGORY_LABELS = {
  street_character: 'Streets & home character',
  market_patterns: 'Market patterns',
  buyer_tips: 'Buyer tips',
  seller_prep: 'Seller prep',
  general: 'General',
};

const NL_COMMUNITY_SUGGESTIONS = [
  "St. John's", 'Mount Pearl', 'Paradise', 'Conception Bay South', 'Torbay',
  "Portugal Cove–St. Philip's", 'Georgestown', 'Downtown St. John\'s', 'Goulds',
  'Bay Roberts', 'Carbonear', 'Holyrood', 'Corner Brook', 'Gander',
  'Grand Falls-Windsor', 'Clarenville', 'Happy Valley-Goose Bay', 'Labrador City',
];

const getSupportedMimeType = () => {
  const candidates = ['audio/webm', 'audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];
  return candidates.find((t) => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || '';
};

function NoteEditor({ note, categories, onSave, onCancel, saving }) {
  const [community, setCommunity] = useState(note.community || '');
  const [category, setCategory] = useState(note.category || 'general');
  const [text, setText] = useState(note.note || '');
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ flex: '1 1 180px' }}
          list="nl-communities"
          placeholder="Community (blank = general)"
          value={community}
          onChange={(e) => setCommunity(e.target.value)}
        />
        <select className="form-input" style={{ flex: '0 1 220px' }} value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
        </select>
      </div>
      <textarea
        className="form-input"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What do you know that a buyer or seller would find valuable?"
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-primary btn-sm" disabled={saving || !text.trim()} onClick={() => onSave({ community: community.trim() || null, category, note: text.trim() })}>
          <Check size={14} /> Save
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function Knowledge() {
  const { agent } = useAuth();
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState(Object.keys(CATEGORY_LABELS));
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);

  // Dictation state: idle | recording | processing
  const [dictation, setDictation] = useState('idle');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const assistantName = agent?.assistant_name || 'Ava';

  const loadNotes = async () => {
    try {
      const res = await api.get('/agent-knowledge');
      setNotes(res.data.notes || []);
      if (res.data.categories?.length) setCategories(res.data.categories);
    } catch (e) {
      toast.error('Could not load knowledge notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadNotes(); }, []);

  const drafts = notes.filter((n) => n.status === 'draft');
  const active = notes.filter((n) => n.status === 'active');
  const activeByCommunity = active.reduce((acc, n) => {
    const key = n.community || 'General (market-wide)';
    (acc[key] = acc[key] || []).push(n);
    return acc;
  }, {});

  const createNote = async (payload) => {
    setSavingId('new');
    try {
      const res = await api.post('/agent-knowledge', payload);
      setNotes((prev) => [res.data.note, ...prev]);
      setAddingNew(false);
      toast.success(`Saved — ${assistantName} can use this now`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not save note', { duration: 8000 });
    } finally {
      setSavingId(null);
    }
  };

  const updateNote = async (id, payload) => {
    setSavingId(id);
    try {
      const res = await api.patch(`/agent-knowledge/${id}`, payload);
      setNotes((prev) => prev.map((n) => (n.id === id ? res.data.note : n)));
      setEditingId(null);
      if (payload.status === 'active') toast.success(`Approved — ${assistantName} can use this now`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not update note', { duration: 8000 });
    } finally {
      setSavingId(null);
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/agent-knowledge/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      toast.error('Could not delete note');
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startDictation = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stopStream();
        setDictation('processing');
        try {
          const type = recorder.mimeType || 'audio/webm';
          const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
          const blob = new Blob(chunksRef.current, { type });
          const formData = new FormData();
          formData.append('audio', blob, `knowledge.${ext}`);
          const sttRes = await api.post('/voice/transcribe', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const { text } = sttRes.data;
          if (!text || text.trim().length < 20) {
            toast.error("Didn't catch enough there — try again and talk a little longer.");
            return;
          }
          const compileRes = await api.post('/agent-knowledge/compile', { transcript: text });
          const newDrafts = compileRes.data.drafts || [];
          const dropped = compileRes.data.dropped || [];
          setNotes((prev) => [...newDrafts, ...prev]);
          if (newDrafts.length) {
            toast.success(`${newDrafts.length} note${newDrafts.length > 1 ? 's' : ''} drafted — review and approve below`);
          } else {
            toast.error('No usable notes came out of that take — try again.');
          }
          if (dropped.length) {
            toast(`Left out (fair-housing rules): ${dropped.join('; ')}`, { icon: '⚖️', duration: 10000 });
          }
        } catch (e) {
          toast.error(e.response?.data?.error || 'Could not process the dictation');
        } finally {
          setDictation('idle');
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setDictation('recording');
    } catch (e) {
      toast.error('Microphone unavailable — check browser permissions');
      stopStream();
      setDictation('idle');
    }
  };

  const stopDictation = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  useEffect(() => () => stopStream(), []);

  const noteRow = (n) => (
    <div key={n.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border, #eee)' }}>
      {editingId === n.id ? (
        <NoteEditor
          note={n}
          categories={categories}
          saving={savingId === n.id}
          onSave={(payload) => updateNote(n.id, payload)}
          onCancel={() => setEditingId(null)}
        />
      ) : (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
              {CATEGORY_LABELS[n.category] || n.category}
              {n.source === 'voice' && ' · dictated'}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>{n.note}</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" title="Edit" onClick={() => setEditingId(n.id)}>
            <Pencil size={13} />
          </button>
          <button type="button" className="btn btn-ghost btn-sm" title="Delete" onClick={() => deleteNote(n.id)}>
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <datalist id="nl-communities">
        {NL_COMMUNITY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
      </datalist>

      <div className="page-header">
        <h2>Market knowledge</h2>
        <p>
          Your local knowledge, in {assistantName}'s answers — always credited to you.
          Type a note, or just talk and {assistantName} will draft notes for your review.
        </p>
      </div>

      {/* Dictation */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h3 className="card-title">Just talk</h3>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
          Tap the mic and talk about an area you know — streets, home character, what's moving,
          tips for buyers or sellers. When you stop, your words are drafted into notes you approve before {assistantName} uses them.
          Safety, school-quality, and demographic comments are automatically left out (fair-housing rules).
          This is for areas and market patterns — details about one of your own listings belong on
          that listing (Properties → edit → "What you know about this home").
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {dictation === 'recording' ? (
            <button type="button" className="btn btn-primary" onClick={stopDictation}>
              <Square size={15} /> Stop — I'm done
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={startDictation} disabled={dictation === 'processing'}>
              {dictation === 'processing' ? <Loader size={15} className="spin" /> : <Mic size={15} />}
              {dictation === 'processing' ? 'Drafting your notes…' : 'Start talking'}
            </button>
          )}
          {dictation === 'recording' && (
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Listening… tap stop when you're done.</span>
          )}
        </div>
      </div>

      {/* Drafts awaiting review */}
      {drafts.length > 0 && (
        <div className="card" style={{ marginBottom: 20, borderLeft: '3px solid var(--accent)' }}>
          <div className="card-header">
            <h3 className="card-title">Review drafts ({drafts.length})</h3>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
            {assistantName} won't use these until you approve them.
          </p>
          {drafts.map((n) => (
            <div key={n.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border, #eee)' }}>
              {editingId === n.id ? (
                <NoteEditor
                  note={n}
                  categories={categories}
                  saving={savingId === n.id}
                  onSave={(payload) => updateNote(n.id, { ...payload, status: 'active' })}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
                      {n.community || 'General'} · {CATEGORY_LABELS[n.category] || n.category}
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5 }}>{n.note}</div>
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" disabled={savingId === n.id} onClick={() => updateNote(n.id, { status: 'active' })}>
                    <Check size={13} /> Approve
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" title="Edit before approving" onClick={() => setEditingId(n.id)}>
                    <Pencil size={13} />
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" title="Discard" onClick={() => deleteNote(n.id)}>
                    <X size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add typed note */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">Your notes</h3>
          {!addingNew && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAddingNew(true)}>
              Add note
            </button>
          )}
        </div>

        {addingNew && (
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border, #eee)' }}>
            <NoteEditor
              note={{ community: '', category: 'general', note: '' }}
              categories={categories}
              saving={savingId === 'new'}
              onSave={createNote}
              onCancel={() => setAddingNew(false)}
            />
          </div>
        )}

        {loading ? (
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
        ) : active.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>
            <BookOpen size={22} style={{ opacity: 0.5 }} />
            <p style={{ fontSize: 13, marginTop: 8 }}>
              No notes yet. What you add here shows up in {assistantName}'s answers, credited to you —
              it's what turns "I don't have local data on that" into "{agent?.first_name || 'Your agent'} knows this area well…"
            </p>
          </div>
        ) : (
          Object.entries(activeByCommunity).map(([community, list]) => (
            <div key={community} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.03em', marginBottom: 2 }}>
                {community}
              </div>
              {list.map(noteRow)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
